import { faExclamationCircle } from '@fortawesome/pro-regular-svg-icons'
import { Tabs, Text } from '@mantine/core'
import { createFormContext, zodResolver } from '@mantine/form'
import { AxiosError } from 'axios'
import { useEffect } from 'react'

import {
  useMutationUpdateBundle,
  useMutationCreateBundle,
} from '@flecto/core/saas'
import { useTranslation } from '@flecto/translations'
import { type CoreBundle } from '@flecto/types'
import { Icon, useNotifications } from '@flecto/ui'
import { useHandleAxiosError } from '@flecto/utils'

import { type CoreBundleForm } from '../../types/CoreBundleForm'
import { TabItems } from './TabItems'
import { TabProperties } from './TabProperties'
import { TabSettings } from './TabSettings'
import { useFormBundleSchema } from './useFormBundleSchema'

export const [FormProvider, useFormContext, useForm] =
  createFormContext<CoreBundleForm>()

type FormBundleProps = {
  id?: string
  bundle?: Partial<CoreBundle>
  onError?: (message: string) => void
  onSuccess?: () => void
  onChangeLoading?: (loading: boolean) => void
  disableSaveButton: (value: boolean) => void
}

export function FormBundle({
  bundle,
  onError,
  onSuccess,
  onChangeLoading,
  disableSaveButton,
  ...props
}: FormBundleProps) {
  const { t } = useTranslation('saas', { keyPrefix: 'items' })

  const { showNotification } = useNotifications()
  const { getErrorMessages } = useHandleAxiosError()

  const { schema, initialValues } = useFormBundleSchema({
    bundle,
  })

  const { mutateAsync: updateBundle } = useMutationUpdateBundle()
  const { mutateAsync: createBundle } = useMutationCreateBundle()

  const form = useForm({
    initialValues,
    validate: zodResolver(schema),
  })

  async function saveBundle() {
    try {
      onChangeLoading?.(true)

      if (form.values.internal_id) {
        await updateBundle({
          bundleId: form.values.internal_id,
          bundle: {
            title: form.values.title ?? '',
            description: form.values.description ?? '',
            bundle_items_attributes: form.values.bundle_items,
            photos_attributes: form.values.photos_attributes,
            category_id: Number(form.values.category_id),
          },
        })
      } else {
        const options =
          form.values.status === 'enabled'
            ? { enable: true }
            : { disable: true }

        const payload = {
          bundle: {
            title: form.values.title ?? '',
            description: form.values.description ?? '',
            bundle_items_attributes: form.values.bundle_items,
            category_id: Number(form.values.category_id),
          },
          options,
        }

        const createdBundle = await createBundle(payload)

        form.setValues({
          internal_id: createdBundle.internal_id,
          status: createdBundle.status,
        })
      }

      showNotification({
        type: 'success',
        message: t('notification_save_bundle_success'),
      })
      onSuccess?.()
    } catch (e) {
      showNotification({
        type: 'error',
        message: t('notification_save_bundle_error'),
      })

      if (e instanceof Error) {
        onError?.(e.message)
      }
      if (e instanceof AxiosError) {
        const errors = getErrorMessages(e, {
          'bundle[photos]': 'photos_attributes',
          'bundle[category]': 'category_id',
        })
        errors.forEach((e) => {
          form.setFieldError(e.field, e.errorMessage)
        })
      }
    } finally {
      onChangeLoading?.(false)
    }
  }

  useEffect(() => {
    if (bundle) {
      form.setValues(initialValues)
    } else {
      form.reset()
    }
  }, [bundle])

  const hasErrorsTabItems = Boolean(form.errors && form.errors.bundle_items)

  const hasErrorsTabProperties = Boolean(
    form.errors &&
      (form.errors.title ||
        form.errors.description ||
        form.errors.category_id ||
        form.errors.photos_attributes)
  )

  const hasErrorsTabSettings = Boolean(form.errors && form.errors.on_store)

  const tabs = [
    {
      id: 'items',
      title: t('label_tab_items'),
      component: <TabItems />,
      hasErrors: hasErrorsTabItems,
    },
    {
      id: 'properties',
      title: t('label_properties'),
      component: <TabProperties />,
      hasErrors: hasErrorsTabProperties,
    },
    {
      id: 'settings',
      title: t('label_settings'),
      component: <TabSettings />,
      hasErrors: hasErrorsTabSettings,
    },
  ]

  return (
    <FormProvider form={form}>
      <form
        {...props}
        onSubmit={async (e) => {
          e.preventDefault()

          form.validate()

          await saveBundle()
        }}
      >
        <Tabs defaultValue='items'>
          <Tabs.List>
            {tabs.map((tab) => (
              <Tabs.Tab
                key={tab.id}
                value={tab.id}
                color={tab.hasErrors ? 'red' : undefined}
                data-testid={`tab-${tab.id}`}
                leftSection={
                  tab.hasErrors ? (
                    <Icon icon={faExclamationCircle} color='red' />
                  ) : undefined
                }
              >
                <Text c={tab.hasErrors ? 'red' : undefined}>{tab.title}</Text>
              </Tabs.Tab>
            ))}
          </Tabs.List>

          {tabs.map((tab) => (
            <Tabs.Panel pt='lg' value={tab.id} key={tab.id}>
              {tab.component}
            </Tabs.Panel>
          ))}
        </Tabs>
      </form>
    </FormProvider>
  )
}
